import { Controller } from "@hotwired/stimulus"

// Global tempo control in the navbar. Single source of truth for BPM across
// the app: persists to localStorage `tmkf-bpm` and broadcasts changes on the
// window `tmkf:bpm` event so WASM islands (the /lab timing visualizer) update
// live. Same-document localStorage writes don't fire `storage`, hence the
// custom event; the `storage` listener only covers other tabs.
const KEY = "tmkf-bpm"
const MIN = 20
const MAX = 300
const DEFAULT = 120

export default class extends Controller {
  static targets = ["label", "slider", "input"]
  declare readonly labelTarget: HTMLElement
  declare readonly sliderTarget: HTMLInputElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly hasLabelTarget: boolean
  declare readonly hasSliderTarget: boolean
  declare readonly hasInputTarget: boolean

  connect(): void {
    this.apply(this.read(), false) // sync UI from storage, no rebroadcast
    window.addEventListener("storage", this.onStorage)
  }

  disconnect(): void {
    window.removeEventListener("storage", this.onStorage)
  }

  // Slider drag / number entry.
  set(event: Event): void {
    const raw = parseFloat((event.target as HTMLInputElement).value)
    if (Number.isNaN(raw)) return
    this.apply(raw, true)
  }

  // Stepper buttons in the studio top bar. They are <button>s, not inputs, so
  // they carry the delta on a data attribute rather than a value.
  step(event: Event): void {
    const el = event.currentTarget as HTMLElement
    const delta = parseFloat(el.dataset.bpmDelta || "0")
    if (Number.isNaN(delta) || delta === 0) return
    this.apply(this.read() + delta, true)
  }

  private apply(value: number, broadcast: boolean): void {
    const bpm = Math.min(MAX, Math.max(MIN, Math.round(value)))
    if (this.hasSliderTarget) this.sliderTarget.value = String(bpm)
    if (this.hasInputTarget) this.inputTarget.value = String(bpm)
    if (this.hasLabelTarget) this.labelTarget.textContent = String(bpm)
    localStorage.setItem(KEY, String(bpm))
    if (broadcast) {
      window.dispatchEvent(new CustomEvent("tmkf:bpm", { detail: bpm }))
    }
  }

  private read(): number {
    const v = parseFloat(localStorage.getItem(KEY) || "")
    return Number.isNaN(v) ? DEFAULT : Math.min(MAX, Math.max(MIN, v))
  }

  private onStorage = (e: StorageEvent): void => {
    if (e.key === KEY && e.newValue) {
      const v = parseFloat(e.newValue)
      if (!Number.isNaN(v)) this.apply(v, false)
    }
  }
}
